/**
 * SettingsAuthSection - encart "Sécurité du compte" pour SettingsPage.
 *
 * Permet a l user :
 *   - de definir un mot de passe (premiere fois)
 *   - de modifier le mot de passe existant (necessite l ancien)
 *   - voir si le compte a actuellement un password ou seulement magic link
 */
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Button,
  Input,
  Label,
  Body1,
  Title3,
  tokens,
  Spinner,
} from '@fluentui/react-components';
import { useAuth } from '../auth/AuthContext';

export function SettingsAuthSection() {
  const { t } = useTranslation();
  const { user, setPassword } = useAuth();
  if (!user) return null;

  const [currentPwd, setCurrentPwd] = useState('');
  const [newPwd, setNewPwd]         = useState('');
  const [confirmPwd, setConfirmPwd] = useState('');
  const [busy, setBusy]             = useState(false);
  const [err, setErr]               = useState<string | null>(null);
  const [ok, setOk]                 = useState<string | null>(null);

  const isFirstTime = !user.hasPassword;

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setErr(null); setOk(null);

    if (newPwd.length < 10) { setErr(t('settings.security.errorTooShort')); return; }
    if (newPwd !== confirmPwd) { setErr(t('settings.security.errorMismatch')); return; }

    setBusy(true);
    try {
      await setPassword(newPwd, isFirstTime ? undefined : currentPwd);
      setOk(isFirstTime ? t('settings.security.successFirst') : t('settings.security.successChange'));
      setCurrentPwd(''); setNewPwd(''); setConfirmPwd('');
    } catch (e) {
      setErr((e as Error)?.message ?? t('settings.security.errorGeneric'));
    } finally {
      setBusy(false);
    }
  }

  return (
    <section
      style={{
        padding:      24,
        background:   tokens.colorNeutralBackground1,
        border:       `1px solid ${tokens.colorNeutralStroke2}`,
        borderRadius: 8,
        marginBottom: 24,
      }}
    >
      <Title3 as="h2" style={{ marginBottom: 4 }}>
        {t('settings.security.title')}
      </Title3>
      <Body1 style={{ color: tokens.colorNeutralForeground3, marginBottom: 20 }}>
        {isFirstTime
          ? t('settings.security.subtitleFirst')
          : t('settings.security.subtitleChange')}
      </Body1>

      <form onSubmit={onSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 12, maxWidth: 420 }}>
        {!isFirstTime && (
          <div>
            <Label htmlFor="current-pwd" required>{t('settings.security.currentPassword')}</Label>
            <Input
              id="current-pwd"
              type="password"
              required
              value={currentPwd}
              onChange={(_e, d) => setCurrentPwd(d.value)}
              style={{ width: '100%', marginTop: 4 }}
            />
          </div>
        )}

        <div>
          <Label htmlFor="new-pwd" required>{t('settings.security.newPassword')}</Label>
          <Input
            id="new-pwd"
            type="password"
            required
            minLength={10}
            value={newPwd}
            onChange={(_e, d) => setNewPwd(d.value)}
            placeholder={t('settings.security.newPasswordPlaceholder')}
            style={{ width: '100%', marginTop: 4 }}
          />
        </div>

        <div>
          <Label htmlFor="confirm-pwd" required>{t('settings.security.confirmPassword')}</Label>
          <Input
            id="confirm-pwd"
            type="password"
            required
            value={confirmPwd}
            onChange={(_e, d) => setConfirmPwd(d.value)}
            style={{ width: '100%', marginTop: 4 }}
          />
        </div>

        {err && (
          <div style={{
            padding: 10, background: tokens.colorPaletteRedBackground1,
            borderLeft: `4px solid ${tokens.colorPaletteRedBorder2}`,
            color: tokens.colorPaletteRedForeground2, fontSize: 13, borderRadius: 4,
          }}>{err}</div>
        )}
        {ok && (
          <div style={{
            padding: 10, background: tokens.colorPaletteGreenBackground1,
            borderLeft: `4px solid ${tokens.colorPaletteGreenBorder2}`,
            color: tokens.colorPaletteGreenForeground2, fontSize: 13, borderRadius: 4,
          }}>{ok}</div>
        )}

        <div>
          <Button type="submit" appearance="primary" disabled={busy} icon={busy ? <Spinner size="tiny" /> : undefined}>
            {busy ? t('settings.security.submitting') : (isFirstTime ? t('settings.security.submitFirst') : t('settings.security.submitChange'))}
          </Button>
        </div>
      </form>
    </section>
  );
}
